import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare, X, Send } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Floating "Suggest improvement" button visible on every authenticated portal page.
 * Submits to POST /api/feedback → super-admin inbox at /admin/feedback.
 *
 * Hidden for: super_admin (they read feedback, don't submit), unauthenticated visitors
 * (demo + landing have their own inline buttons).
 */
export default function FeedbackWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  if (user.role === "super_admin") return null;

  const submit = async () => {
    const t = text.trim();
    if (!t) return toast.error("Please type your feedback first.");
    if (t.length > 3000) return toast.error("Max 3000 characters.");
    setBusy(true);
    try {
      await http.post("/feedback", {
        text: t,
        source: "portal",
        page_context: location.pathname,
      });
      toast.success("🙏 Thanks! Your feedback reached the VidyaAI Study team.");
      setText(""); setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1A1713] text-[#FDFBF7] shadow-lg hover:bg-[#2A2520] transition-colors text-sm font-medium"
          data-testid="feedback-widget-open"
          title="Suggest an improvement"
        >
          <MessageSquare size={16} strokeWidth={1.8} />
          <span className="hidden sm:inline">Feedback</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] max-w-sm bg-[#FDFBF7] rounded-2xl border border-[#E6E2D8] shadow-2xl" data-testid="feedback-widget-panel">
          <div className="flex items-center justify-between p-4 border-b border-[#E6E2D8]">
            <div className="flex items-center gap-2">
              <MessageSquare size={16} className="text-[#C24933]" />
              <div className="font-display text-base">Suggest improvement</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#7A7369] hover:text-[#1A1713] p-1" data-testid="feedback-widget-close">
              <X size={16} />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-xs text-[#7A7369]">
              What would make VidyaAI Study better for you? Bugs, feature ideas, or anything else — your message goes straight to the founders.
            </p>
            <textarea
              className="input-field min-h-[110px] text-sm"
              placeholder="Type your feedback here…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={3000}
              data-testid="feedback-widget-textarea"
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-[#7A7369]">{text.length}/3000</span>
              <button
                onClick={submit} disabled={busy || !text.trim()}
                className="btn-primary text-sm py-2 px-4"
                data-testid="feedback-widget-submit"
              >
                {busy ? "Sending…" : <><Send size={14} /> Send feedback</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
